import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // add this at the top
import axios from "axios";
import Select from "../../components/ReportSelect";
import "../../styles/Reports.css";

import {
  Container,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Form,
} from "react-bootstrap";
import { saveAs } from "file-saver";
import Pagination from "../../components/Pagination";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ALL_COLUMNS = [
  { key: "usereid", label: "ID Number" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Phone" },
  { key: "address1", label: "Address" },
  { key: "address2", label: "Address Line 2" },
  { key: "city", label: "City" },
  { key: "province", label: "Province" },
  { key: "country", label: "Country" },
  { key: "postal_code", label: "Postal Code" },
  { key: "sin", label: "SIN" },
  { key: "dob", label: "Birth Date" },
  { key: "truck_info", label: "Truck Information" },
  { key: "org_unit", label: "Org Unit" },
  { key: "position", label: "Position" },
  { key: "emp_sub_category", label: "Employment Sub-Category" },
  { key: "company_name", label: "Company Name" },
  { key: "dl_license_no", label: "Licence Number" },
  { key: "dl_expdate", label: "Licence Expiry" },
  { key: "dl_province", label: "Licence Province" },
  { key: "passport_number", label: "Passport Number" },
  { key: "fast_card_number", label: "FAST Card Number" },
  { key: "tags", label: "Tags" },
  { key: "associate_anniversary", label: "Associate Anniversary" },
];

const ADDRESS_KEYS = [
  "address1",
  "address2",
  "city",
  "province",
  "country",
  "postal_code",
];

const DEFAULT_VISIBLE_KEYS = [
  "usereid",
  "first_name",
  "last_name",
  "email",
  "mobile",
  "address1",
  "emp_sub_category",
];

export default function ContactListReport() {
  const navigate = useNavigate(); // hook for navigation

  const [data, setData] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [loading, setLoading] = useState(false);
  const [tagLookup, setTagLookup] = useState({});
  const [filters, setFilters] = useState({
    category: [],
    status: [],
    subCategory: [],
    orgUnit: [],
    tag: [],
  });

  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.reduce((acc, col) => {
      acc[col.key] = DEFAULT_VISIBLE_KEYS.includes(col.key);
      return acc;
    }, {})
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const itemsPerPage = 10;

  const fetchDropdowns = async () => {
    const token = localStorage.getItem("token");
    try {
      const [cat, stat, sub, org, tag] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/api/dropdowns/user-status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/api/dropdowns/org-units`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/api/dropdowns/tags`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setDropdowns({
        categories: cat.data,
        statuses: stat.data,
        subCategories: sub.data,
        orgUnits: org.data,
        tags: tag.data,
      });
      setTagLookup(
        tag.data.reduce((acc, t) => {
          acc[t.t_id] = t.t_name;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Dropdown fetch failed", err);
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const handleMultiSelectChange = (key, selected) => {
    setFilters((prev) => ({
      ...prev,
      [key]: selected.map((s) => s.value),
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: [],
      status: [],
      subCategory: [],
      orgUnit: [],
      tag: [],
    });
    setData([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const fetchData = async () => {
    setLoading(true);
    setHasSearched(true);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${BASE_URL}/api/reports/contact-list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          category: filters.category.join(","),
          status: filters.status.join(","),
          subCategory: filters.subCategory.join(","),
          orgUnit: filters.orgUnit.join(","),
          tag: filters.tag.join(","),
        },
      });
      // Filter users that have at least one selected tag in tags_with_value
      const filtered = res.data.filter((user) => {
        if (filters.tag.length === 0) return true;
        const userTags = Object.keys(user.tags_with_value || {}).map((id) =>
          parseInt(id)
        );
        return filters.tag.some((tagId) => userTags.includes(tagId));
      });

      setData(filtered);
      setCurrentPage(1);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveVisibleColumns = () => {
    const result = { ...visibleColumns };
    if (visibleColumns["address1"]) {
      ADDRESS_KEYS.forEach((key) => (result[key] = true));
    } else {
      ADDRESS_KEYS.forEach((key) => (result[key] = false));
    }
    return ALL_COLUMNS.filter((col) => result[col.key]);
  };

  const exportCSV = () => {
    if (data.length === 0) {
      alert("No data found");
      return;
    }

    const cols = getEffectiveVisibleColumns();
    const headers = cols
      .map((col) =>
        col.label === "ID Number"
          ? `"${col.label}"`
          : col.key === "country"
          ? "Country"
          : col.label
      )
      .join(",");
    const rows = data.map((row) =>
      cols
        .map((col) => {
          const key = col.key;
          let val = row[key];

          if (
            ["dob", "associate_anniversary", "dl_expdate"].includes(col.key) &&
            val
          ) {
            val = new Date(val).toISOString().split("T")[0];
          }

          if (key === "tags" && Array.isArray(row.tags)) {
            val = row.tags
              .map((tagId) => {
                const name = tagLookup[tagId] || `ID ${tagId}`;
                const value = row.tags_with_value?.[tagId] ?? "";
                return `${name}${value ? " - " + value : ""}`;
              })
              .join(" | ");
          }

          if (key === "position") {
            const id = row.id;
            const position = row.position;
            const orgUnit = row.org_unit;
            val = `${id} - ${position}${orgUnit ? " - " + orgUnit : ""}`;
          }

          if (key === "country") {
            if (val === 1 || val === "1") val = "Canada";
            // Add more country codes here if needed
          }

          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(",")
    );

    if (rows.length === 0) {
      alert("No rows to export");
      return;
    }

    const blob = new Blob([[headers, ...rows].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, "ContactListReport.csv");
  };

  const toggleColumn = (key) =>
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderMultiSelect = (key, options, labelKey, valueKey) => (
    <Select
      isMulti
      options={options.map((o) => ({
        label: o[labelKey],
        value: o[valueKey],
      }))}
      value={options
        .filter((o) => filters[key].includes(o[valueKey]))
        .map((o) => ({
          label: o[labelKey],
          value: o[valueKey],
        }))}
      onChange={(selected) => handleMultiSelectChange(key, selected)}
    />
  );

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="fw-bold m-0">📅 New Contact List</h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")} // adjust route if different
        >
          ⬅ Return to Report List
        </Button>
      </div>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Applicant Type :</label>
          {renderMultiSelect(
            "category",
            dropdowns.categories || [],
            "c_name",
            "c_id"
          )}
        </Col>
        <Col>
          <label>Status :</label>
          {renderMultiSelect(
            "status",
            dropdowns.statuses || [],
            "s_TranslatedText",
            "s_id"
          )}
        </Col>
        <Col>
          <label>Employment Sub-Category :</label>
          {renderMultiSelect(
            "subCategory",
            dropdowns.subCategories || [],
            "sc_name",
            "sc_id"
          )}
        </Col>
        <Col>
          <label>Org Unit :</label>
          {renderMultiSelect(
            "orgUnit",
            dropdowns.orgUnits || [],
            "ou_name",
            "ou_id"
          )}
        </Col>
        <Col>
          <label>Tags :</label>
          {renderMultiSelect("tag", dropdowns.tags || [], "t_name", "t_id")}
        </Col>
        <Col md="auto" className="align-end">
          <div className="button-group">
            <Button variant="primary" onClick={fetchData}>
              🔍 Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>
      </Row>

      <div className="border rounded p-3 mb-3">
        <h6 className="fw-bold">Select Columns</h6>
        <Row>
          {ALL_COLUMNS.filter(
            (col) => !ADDRESS_KEYS.includes(col.key) || col.key === "address1"
          ).map((col) => (
            <Col xs={6} md={3} key={col.key}>
              <Form.Check
                type="checkbox"
                label={col.key === "address1" ? "Address" : col.label}
                checked={visibleColumns[col.key]}
                onChange={() => toggleColumn(col.key)}
              />
            </Col>
          ))}
        </Row>
      </div>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md="auto" className="align-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || data.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                {getEffectiveVisibleColumns().map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, i) => (
                <tr key={i}>
                  {getEffectiveVisibleColumns().map((col) => (
                    <td key={col.key} className="text-center">
                      {col.key === "tags" && Array.isArray(row.tags)
                        ? row.tags
                            .map((tagId) => {
                              const name = tagLookup[tagId] || `ID ${tagId}`;
                              const value = row.tags_with_value?.[tagId] ?? "";
                              return `${name}${value ? " - " + value : ""}`;
                            })
                            .join(", ")
                        : col.key === "position"
                        ? `${row.id} - ${row.position}${
                            row.org_unit ? " - " + row.org_unit : ""
                          }`
                        : col.key === "country"
                        ? row.country_name || row.country
                        : [
                            "dob",
                            "associate_anniversary",
                            "dl_expdate",
                          ].includes(col.key) && row[col.key]
                        ? new Date(row[col.key]).toISOString().split("T")[0]
                        : Array.isArray(row[col.key])
                        ? row[col.key].join(" | ")
                        : row[col.key] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalItems={data.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
